<template>
  <b-row class="flow-chart">
    <b-col>
      <dashboard-flow-chart :nodes="nodes" />
    </b-col>
  </b-row>
</template>

<script>
import DashboardFlowChart from '@/components/Dashboard/DashboardFlowChart';
import StatCircle from '@/components/Dashboard/StatCircle';
import StatBox from '@/components/Dashboard/StatBox';
import DashboardHierarchyMixin from '@/components/Dashboard/DashboardHierarchyMixin';

const VALIDATION_BOX_STYLES = {
  bgColor: '#EFF3F8', labelColor: '#2C3E38', color: '#638BC2', align: 'center', size: 'x-small', cssClass: 'py-1 stat-box',
};
const IN_PROGRESS_BOX_STYLES = {
  bgColor: '#F2F5F8', labelColor: '#2C3E38', color: '#678DA9', align: 'center', size: 'x-small', cssClass: 'py-1 stat-box',
};
const QUERIED_BOX_STYLES = {
  bgColor: '#FFF6E5', labelColor: '#2C3E38', color: '#FFA600', align: 'center', size: 'x-small', cssClass: 'py-1 stat-box',
};

export default {
  name: 'PropertyCountHierarchy',
  components: {
    DashboardFlowChart,
  },
  props: {
    counts: {
      type: Object,
      required: true,
    },
  },
  mixins: [DashboardHierarchyMixin],
  data() {
    return {
      nodes: [
        {
          id: '1',
          parentId: -1,
          nodeComponent: StatCircle,
          data: {
            value: 0,
            bgColor: '#F2F5F8',
            icon: 'propertymanagement',
            label: 'PROPERTIES IN JOBS',
            labelClass: 'text-dark',
            to: {
              name: 'programme',
              query: {
                assessmentStatus: [
                  'Submitted for Desktop Review',
                  'Submitted for Validation',
                  'Stage 1 Check Complete',
                  'Stage 2 Check Complete',
                  'Returned from Validation',
                  'Finalised',
                  'Rolled Back',
                  'Assigned',
                  'Appointment Booked',
                  'Inspection Started',
                  'Inspection Paused',
                  'Inspection Finished',
                  'Being Written Up',
                  'No Answer - Attempt 1',
                  'No Answer - Attempt 2',
                  'Queried',
                  'Unassigned',
                  'No Access',
                ],
              },
              params: { title: 'Properties in Jobs' },
            },
          },
        },
        {
          id: '2',
          parentId: '1',
          nodeComponent: StatCircle,
          data: {
            value: 0,
            color: '#FFFFFF',
            bgColor: '#638BC2',
            label: 'VALIDATION',
            to: {
              name: 'programme',
              query: {
                assessmentStatus: [
                  'Submitted for Desktop Review',
                  'Submitted for Validation',
                  'Stage 1 Check Complete',
                  'Stage 2 Check Complete',
                  'Returned from Validation',
                  'Finalised',
                  'Rolled Back',
                ],
              },
              params: { title: 'Validation' },
            },
          },
        },
        {
          id: '3',
          parentId: '1',
          nodeComponent: StatCircle,
          data: {
            value: 0,
            color: '#FFFFFF',
            bgColor: '#678DA9',
            label: 'IN PROGRESS',
            to: {
              name: 'programme',
              query: {
                assessmentStatus: [
                  'Assigned',
                  'Appointment Booked',
                  'Inspection Started',
                  'Inspection Paused',
                  'Inspection Finished',
                  'Being Written Up',
                  'No Answer - Attempt 1',
                  'No Answer - Attempt 2',
                ],
              },
              params: { title: 'In Progress' },
            },
          },
        },
        {
          id: '4',
          parentId: '1',
          nodeComponent: StatCircle,
          data: {
            value: 0,
            color: '#FFFFFF',
            bgColor: '#FFA600',
            label: 'QUERIED',
            to: {
              name: 'programme',
              query: {
                queryType: [
                  'Void',
                  'Contact Unresponsive',
                  'Other',
                  'Resident Refused Access',
                  'Failed Appointment',
                  'No Answer',
                ],
              },
              params: { title: 'Queried' },
            },
          },
        },
        {
          id: '5',
          parentId: '1',
          nodeComponent: StatCircle,
          data: {
            value: 0,
            color: '#FFFFFF',
            bgColor: '#E87D5C',
            label: 'UNASSIGNED',
            to: {
              name: 'programme',
              query: { assessmentStatus: 'Unassigned' },
            },
          },
        },
        {
          id: 'submitted for desktop review',
          parentId: '2',
          nodeComponent: StatBox,
          data: {
            value: 0,
            ...VALIDATION_BOX_STYLES,
            label: 'SUBMITTED FOR DESKTOP REVIEW',
            to: {
              name: 'programme',
              query: { assessmentStatus: 'Submitted for Desktop Review' },
            },
          },
        },
        {
          id: 'submitted for validation',
          parentId: 'submitted for desktop review',
          nodeComponent: StatBox,
          data: {
            value: 0,
            ...VALIDATION_BOX_STYLES,
            label: 'SUBMITTED FOR VALIDATION',
            to: {
              name: 'programme',
              query: { assessmentStatus: 'Submitted for Validation' },
            },
          },
        },
        {
          id: 'stage 1 check complete',
          parentId: 'submitted for validation',
          nodeComponent: StatBox,
          data: {
            value: 0,
            ...VALIDATION_BOX_STYLES,
            label: 'STAGE ONE CHECKED',
            to: {
              name: 'programme',
              query: { assessmentStatus: 'Stage 1 Check Complete' },
            },
          },
        },
        {
          id: 'stage 2 check complete',
          parentId: 'stage 1 check complete',
          nodeComponent: StatBox,
          data: {
            value: 0,
            ...VALIDATION_BOX_STYLES,
            label: 'STAGE TWO CHECKED',
            to: {
              name: 'programme',
              query: { assessmentStatus: 'Stage 2 Check Complete' },
            },
          },
        },
        {
          id: 'returned from validation',
          parentId: 'stage 2 check complete',
          nodeComponent: StatBox,
          data: {
            value: 0,
            ...VALIDATION_BOX_STYLES,
            label: 'RETURNED FROM VALIDATION',
            to: {
              name: 'programme',
              query: { assessmentStatus: 'Returned from Validation' },
            },
          },
        },
        {
          id: 'finalised',
          parentId: 'returned from validation',
          nodeComponent: StatBox,
          data: {
            value: 0,
            ...VALIDATION_BOX_STYLES,
            label: 'FINALISED',
            to: {
              name: 'programme',
              query: { assessmentStatus: 'Finalised' },
            },
          },
        },
        {
          id: 'rolled back',
          parentId: 'finalised',
          nodeComponent: StatBox,
          data: {
            value: 0,
            ...VALIDATION_BOX_STYLES,
            label: 'ROLLED BACK',
            to: {
              name: 'programme',
              query: { assessmentStatus: 'Rolled Back' },
            },
          },
        },
        {
          id: 'assigned',
          parentId: '3',
          nodeComponent: StatBox,
          data: {
            value: 0,
            ...IN_PROGRESS_BOX_STYLES,
            label: 'ASSIGNED',
            to: {
              name: 'programme',
              query: { assessmentStatus: 'Assigned' },
            },
          },
        },
        {
          id: 'appointment booked',
          parentId: 'assigned',
          nodeComponent: StatBox,
          data: {
            value: 0,
            ...IN_PROGRESS_BOX_STYLES,
            label: 'APPOINTMENT BOOKED',
            to: {
              name: 'programme',
              query: { assessmentStatus: 'Appointment Booked' },
            },
          },
        },
        {
          id: 'inspection started',
          parentId: 'appointment booked',
          nodeComponent: StatBox,
          data: {
            value: 0,
            ...IN_PROGRESS_BOX_STYLES,
            label: 'INSPECTION STARTED',
            to: {
              name: 'programme',
              query: { assessmentStatus: 'Inspection Started' },
            },
          },
        },
        {
          id: 'inspection paused',
          parentId: 'inspection started',
          nodeComponent: StatBox,
          data: {
            value: 0,
            ...IN_PROGRESS_BOX_STYLES,
            label: 'INSPECTION PAUSED',
            to: {
              name: 'programme',
              query: { assessmentStatus: 'Inspection Paused' },
            },
          },
        },
        {
          id: 'inspection finished',
          parentId: 'inspection paused',
          nodeComponent: StatBox,
          data: {
            value: 0,
            ...IN_PROGRESS_BOX_STYLES,
            label: 'INSPECTION FINISHED',
            to: {
              name: 'programme',
              query: { assessmentStatus: 'Inspection Finished' },
            },
          },
        },
        {
          id: 'being written up',
          parentId: 'inspection finished',
          nodeComponent: StatBox,
          data: {
            value: 0,
            ...IN_PROGRESS_BOX_STYLES,
            label: 'BEING WRITTEN UP',
            to: {
              name: 'programme',
              query: { assessmentStatus: 'Being Written Up' },
            },
          },
        },
        {
          id: 'no answer - attempt 1',
          parentId: 'being written up',
          nodeComponent: StatBox,
          data: {
            value: 0,
            ...IN_PROGRESS_BOX_STYLES,
            label: 'NO ANSWER - ATTEMPT 1',
            to: {
              name: 'programme',
              query: { assessmentStatus: 'No Answer - Attempt 1' },
            },
          },
        },
        {
          id: 'no answer - attempt 2',
          parentId: 'no answer - attempt 1',
          nodeComponent: StatBox,
          data: {
            value: 0,
            ...IN_PROGRESS_BOX_STYLES,
            label: 'NO ANSWER - ATTEMPT 2',
            to: {
              name: 'programme',
              query: { assessmentStatus: 'No Answer - Attempt 2' },
            },
          },
        },
        {
          id: 'failed appointment',
          parentId: '4',
          nodeComponent: StatBox,
          data: {
            value: 0,
            ...QUERIED_BOX_STYLES,
            label: 'FAILED APPOINTMENT',
            to: {
              name: 'programme',
              query: { queryType: 'Failed Appointment' },
            },
          },
        },
        {
          id: 'no answer',
          parentId: 'failed appointment',
          nodeComponent: StatBox,
          data: {
            value: 0,
            ...QUERIED_BOX_STYLES,
            label: 'NO ANSWER',
            to: {
              name: 'programme',
              query: { queryType: 'No Answer' },
            },
          },
        },
        {
          id: 'other',
          parentId: 'no answer',
          nodeComponent: StatBox,
          data: {
            value: 0,
            ...QUERIED_BOX_STYLES,
            label: 'OTHER',
            to: {
              name: 'programme',
              query: { queryType: 'Other' },
            },
          },
        },
        {
          id: 'resident refused access',
          parentId: 'other',
          nodeComponent: StatBox,
          data: {
            value: 0,
            ...QUERIED_BOX_STYLES,
            label: 'RESIDENT REFUSED ACCESS',
            to: {
              name: 'programme',
              query: { queryType: 'Resident Refused Access' },
            },
          },
        },
        {
          id: 'void',
          parentId: 'resident refused access',
          nodeComponent: StatBox,
          data: {
            value: 0,
            ...QUERIED_BOX_STYLES,
            label: 'VOID',
            to: {
              name: 'programme',
              query: { queryType: 'Void' },
            },
          },
        },
        {
          id: 'contact unresponsive',
          parentId: 'void',
          nodeComponent: StatBox,
          data: {
            value: 0,
            ...QUERIED_BOX_STYLES,
            label: 'CONTACT UNRESPONSIVE',
            to: {
              name: 'programme',
              query: { queryType: 'Contact Unresponsive' },
            },
          },
        },
      ],
    };
  },
  computed: {
    total() {
      if (!this.counts) {
        return 0;
      }

      const { assessmentStatus } = this.counts;
      return this.validation + this.inProgress + this.queried + assessmentStatus.unassigned;
    },
    validation() {
      if (!this.counts) {
        return 0;
      }

      const { assessmentStatus } = this.counts;

      return assessmentStatus['submitted for desktop review']
          + assessmentStatus['submitted for validation']
          + assessmentStatus['stage 1 check complete']
          + assessmentStatus['stage 2 check complete']
          + assessmentStatus['returned from validation']
          + assessmentStatus.finalised
          + assessmentStatus['rolled back'];
    },
    inProgress() {
      if (!this.counts) {
        return 0;
      }

      const { assessmentStatus } = this.counts;

      return assessmentStatus.assigned
        + assessmentStatus['appointment booked']
        + assessmentStatus['inspection started']
        + assessmentStatus['inspection paused']
        + assessmentStatus['inspection finished']
        + assessmentStatus['being written up']
        + assessmentStatus['no answer - attempt 1']
        + assessmentStatus['no answer - attempt 2'];
    },
    queried() {
      const { queried } = this.counts;

      return queried['resident refused access']
        + queried['failed appointment']
        + queried['no answer']
        + queried.void
        + queried['contact unresponsive']
        + queried.other;
    },
  },
  created() {
    this.updateCounts();
  },
  methods: {
    updateCounts() {
      const { assessmentStatus, queried } = this.counts;

      // Level 1
      this.nodes[0].data.value = this.total;

      // Level 2
      this.nodes[1].data.value = this.validation;
      this.nodes[2].data.value = this.inProgress;
      this.nodes[3].data.value = this.queried;
      this.nodes[4].data.value = assessmentStatus.unassigned;

      // Level 3
      this.updateValue('submitted for desktop review', assessmentStatus['submitted for desktop review']);
      this.updateValue('submitted for validation', assessmentStatus['submitted for validation']);
      this.updateValue('stage 1 check complete', assessmentStatus['stage 1 check complete']);
      this.updateValue('stage 2 check complete', assessmentStatus['stage 2 check complete']);
      this.updateValue('returned from validation', assessmentStatus['returned from validation']);
      this.updateValue('finalised', assessmentStatus.finalised);
      this.updateValue('rolled back', assessmentStatus['rolled back']);

      this.updateValue('assigned', assessmentStatus.assigned);
      this.updateValue('appointment booked', assessmentStatus['appointment booked']);
      this.updateValue('inspection started', assessmentStatus['inspection started']);
      this.updateValue('inspection paused', assessmentStatus['inspection paused']);
      this.updateValue('inspection finished', assessmentStatus['inspection finished']);
      this.updateValue('being written up', assessmentStatus['being written up']);
      this.updateValue('no answer - attempt 1', assessmentStatus['no answer - attempt 1']);
      this.updateValue('no answer - attempt 2', assessmentStatus['no answer - attempt 2']);

      this.updateValue('failed appointment', queried['failed appointment']);
      this.updateValue('no answer', queried['no answer']);
      this.updateValue('other', queried.other);
      this.updateValue('resident refused access', queried['resident refused access']);
      this.updateValue('void', queried.void);
      this.updateValue('contact unresponsive', queried['contact unresponsive']);

      this.filterHierarchy('submitted for desktop review');
      this.filterHierarchy('assigned');
      this.filterHierarchy('failed appointment');
    },
  },
};
</script>

<style scoped>

</style>
